import { createLazyFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { LoadingPage } from "@/components/LoadingPage";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { Section } from "@/features/admin/components/Section";
import { FieldSpecDetailForm } from "@/features/admin/components/tubes/FieldSpecDetailForm";
import { useTabSearch } from "@/features/admin/hooks";
import { mutationResponse } from "@/utils/trpc";

export const Route = createLazyFileRoute("/_auth/admin/tubes/fields/$id/edit")({
  component: EditFieldSpecPage,
});

function EditFieldSpecPage() {
  const params = Route.useParams();
  const tabsProps = useTabSearch();
  const { data, isPending, error } =
    trpc.admin.sampleFieldSpec.get.useQuery(params);

  if (isPending) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <Centered>
        <Text isDanger>{error.message}</Text>
      </Centered>
    );
  }

  const fieldSpec = data;

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb href={{ to: "/admin/tubes" }}>Tubes</Breadcrumb>
            <Breadcrumb
              href={{ to: "/admin/tubes", search: { t: "custom-fields" } }}
            >
              Custom fields
            </Breadcrumb>
            <Breadcrumb>{fieldSpec.name}</Breadcrumb>
          </>
        }
        actions={
          <DeleteFieldSpecDialog fieldSpec={fieldSpec}>
            <Button variant="destructive">Delete</Button>
          </DeleteFieldSpecDialog>
        }
      >
        {fieldSpec.name}
      </Section.Title>
      <Tabs {...tabsProps} className="flex-1">
        <TabsList>
          <Tab id="details">Details</Tab>
          {/* <Tabs.Tab id="fields">Fields</Tabs.Tab> */}
        </TabsList>
        <TabPanel id="details">
          <FieldSpecDetailForm field={fieldSpec} />
        </TabPanel>
        {/* <Tabs.Panel id="fields" className="flex flex-col flex-1 mt-4">
          <FieldSetFieldsTable fieldSet={fieldSet} />
        </Tabs.Panel> */}
      </Tabs>
    </>
  );
}

function DeleteFieldSpecDialog({
  children,
  fieldSpec,
}: {
  children: ReactNode;
  fieldSpec: { id: number };
}) {
  const [open, setOpen] = useState(false);
  const deleteFieldSpec = trpc.admin.sampleFieldSpec.delete.useMutation();
  const navigate = useNavigate();

  const onConfirm = () => {
    deleteFieldSpec.mutate(
      fieldSpec,
      mutationResponse({
        errorTitle: "Error deleting field spec",
        successMessage: "Field spec deleted",
        onSuccess() {
          setOpen(false);
          navigate({ to: "/admin/tubes" });
        },
      }),
    );
  };

  return (
    <ConfirmPrompt
      danger
      open={open}
      onOpenChange={setOpen}
      title="Delete field spec"
      message="Are you sure you want to delete this field spec?"
      onConfirm={onConfirm}
      loading={deleteFieldSpec.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}
